<template>
  <template v-if="config.presetRanges">
    <ak-field addons>
      <input
        ref="input"
        type="text"
        data-input="true"
        :disabled="disabled"
        :required="required"
        class="ak-date-picker ak-date-picker--has-preset-range"
        :class="{ 'ak-date-picker--inline': isInline }"
        @input="onInput"
      />
      <ak-button color="grey-lighter" single-icon @click.stop.prevent="open">
        <ak-icon type="list" size="big" />
      </ak-button>
    </ak-field>
  </template>

  <template v-else>
    <input
      ref="input"
      type="text"
      data-input="true"
      :disabled="disabled"
      :required="required"
      class="ak-date-picker"
      :class="{ 'ak-date-picker--inline': isInline }"
      @input="onInput"
    />
  </template>
</template>

<script>
import {
  defineComponent,
  ref,
  computed,
  nextTick,
  onMounted,
  onBeforeUnmount,
  watch,
} from 'vue'

import { datePickerProps, validationProps } from '../../mixins/formPropsMixin'
import { includedDateEvents, excludedDateEvents } from '../../utils/config'
import { camelToKebab, arrayify, cloneObject } from '../../utils/helpers'
import validationMixin from '../../mixins/validationMixin'
import DateRangePlugin from './rangePlugin/index.js'
import Flatpickr from 'flatpickr'
import { hasOwn } from '../../utils/helpers'

const allEvents = includedDateEvents.concat(excludedDateEvents)
const configCallbacks = ['locale', 'showMonths']

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-datepicker />
   */
  name: 'AkDatePicker',

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    ...datePickerProps,
    ...validationProps,
  },

  /**
   * @type {Array}
   * @description The list of events that this component can trigger
   */
  emits: [
    'blur',
    'change',
    'open',
    'close',
    'monthChange',
    'yearChange',
    'ready',
    'valueUpdate',
    'update:modelValue',
  ],

  /**
   * @param {Object} props - The properties of the component
   * @param {Object} options - The setup options
   * @param {Function} options.emit - The emit method for triggering events
   * @param {Function} options.attrs - The attributes for the component
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props, { emit, attrs }) {
    const input = ref(null)
    const picker = ref(null)
    const isValid = ref(true)
    const errorClass = 'ak-date-picker--validation-danger'

    /**
     * @type {Object}
     * @description A reactive object the will act as the model for
     * the value of the component, this will update the parent when
     * the date is updated.
     */
    const computedValue = computed({
      get() {
        return props.modelValue
      },
      set(value) {
        emit('update:modelValue', value)
      },
    })

    /**
     * @type {Object}
     * @description A reactive object containing the boolean flag
     * that tells us if the date picker instance is inline or not
     */
    const isInline = computed(() => {
      return props.config.inline
    })

    /**
     * @returns {Object}
     * @description Returns the Flatpickr instance input
     */
    const instInput = () => {
      return picker.value.altInput || picker.value.input
    }

    /**
     * @returns {Object}
     * @description Returns the root element or the wrapped element
     * if the Flatpickr instance is configured to be wrapped.
     */
    const getElement = () => {
      const element = props.config.wrap ? input.value.parentNode : input.value
      return element
    }

    /**
     * @description A simple function we can bind to a button to open the
     * datepicker
     */
    const open = () => {
      if (!picker.value.isOpen) {
        if (props.config.presetRanges) {
          picker.value.openPresetRange()
        } else {
          picker.value.open()
        }
      }
    }

    /**
     * @type {Object}
     * @description The validation mixin return value, this is an object
     * containing all of the required functionality to handle the
     * validation of the input
     */
    const validation = validationMixin({
      type: 'date',
      emit,
      props,
      attrs,
      input: ref({
        validationMessage:
          props.validationMessage ||
          'Please choose a date if you want to proceed.',
        customRuleValidationMessage: '',
        setCustomValidity(message) {
          this.customRuleValidationMessage = message
          if (message.length > 0) isValid.value = false
          else isValid.value = true
        },
        checkValidity() {
          const hasCustomRule =
            hasOwn(props, 'customRules') && props.customRules.length > 0
          const value = props.config.wrap ? input.value.parentNode : input.value

          if (hasCustomRule && !isValid.value) {
            this.validationMessage =
              this.customRuleValidationMessage.length > 0
                ? this.customRuleValidationMessage
                : 'Please choose an option if you want to proceed.'
            picker.value.element.classList.add(errorClass)
            return false
          }

          if (props.required && !value.value.length) {
            if (!validation.isPristine.value) {
              picker.value.element.classList.add(errorClass)
            }
            isValid.value = false
            return false
          } else {
            picker.value.element.classList.remove(errorClass)
            isValid.value = true
            return true
          }
        },
        get value() {
          return (props.config.wrap ? input.value.parentNode : input.value)
            .value
        },
      }),
    })

    /**
     * @param {Event} event
     * @description Event handler for the input change we will emit
     * the input event to update the parent model value.
     */
    const onInput = (event) => {
      const { value } = event.target
      nextTick(() => {
        computedValue.value = value
        validation.validate(true)
      })
    }

    /**
     * @param {Event} event
     * @description Event handler for the blur event on the input
     */
    const onBlur = (event) => {
      const { value } = event.target
      emit('blur', value)
    }

    /**
     * @param {Boolean} newState
     * @description Watcher function for the disabled state, we are using
     * the watchers this way as if we use the default watcher object, this
     * will throw errors as the Flatpickr instance may not be set.
     */
    const watchDisabled = (newState) => {
      if (newState) {
        instInput().setAttribute('disabled', newState)
      } else {
        instInput().removeAttribute('disabled')
      }
    }

    /**
     * @type {Object}
     * @description an object containing the hooks for the flatpikr
     * instance
     */
    const hooks = {
      /**
       * onOpen gets triggered when the calendar is opened.
       * @param  {...any} args
       */
      onOpen(...args) {
        emit('open', ...args)
      },

      /**
       * onClose gets triggered when the calendar is closed.
       * @param  {...any} args
       */
      onClose(...args) {
        emit('open', ...args)
      },

      /**
       * onMonthChange gets triggered when the month is changed, either by the
       *  user or programmatically.
       * @param  {...any} args
       */
      onMonthChange(...args) {
        emit('monthChange', ...args)
      },

      /**
       * onYearChange gets triggered when the year is changed, either by the user
       * or programmatically.
       * @param  {...any} args
       */
      onYearChange(...args) {
        emit('yearChange', ...args)
      },

      /**
       * onReady gets triggered once the calendar is in a ready state.
       * @param  {...any} args
       */
      onReady(...args) {
        emit('ready', ...args)
      },

      /**
       * onValueUpdate gets triggered when the input value is updated with a new
       * date string.
       * @param  {...any} args
       */
      onValueUpdate(...args) {
        emit('valueUpdate', ...args)
      },

      /**
       * @description onChange gets triggered when the user selects a date,
       * or changes the time on a selected date.
       * @param  {...any} args
       */
      onChange(...args) {
        emit('change', ...args)
      },
    }

    /**
     * @type {Function}
     * @description Sets up the flatpicker date picker instance.
     */
    const setupPicker = () => {
      if (picker.value) return
      let safeConfig = cloneObject(props.config)

      props.events.forEach((hook) => {
        let globalCallbacks = Flatpickr.defaultConfig[hook] || []
        let localCallback = (...args) => {
          emit(camelToKebab(hook), ...args)
        }
        safeConfig[hook] = arrayify(safeConfig[hook] || []).concat(
          globalCallbacks,
          localCallback
        )
      })

      safeConfig.defaultDate = props.modelValue || safeConfig.defaultDate

      if (Array.isArray(safeConfig.plugins)) {
        safeConfig.plugins = [...safeConfig.plugins]
      } else {
        safeConfig.plugins = []
      }

      if (safeConfig.presetRanges) {
        safeConfig.plugins.push(DateRangePlugin({}))
      }

      for (const [key, value] of Object.entries(hooks)) {
        safeConfig[key] = value
      }

      picker.value = new Flatpickr(getElement(), safeConfig || {})
      instInput().addEventListener('blur', onBlur)
      watch(() => props.disabled, watchDisabled, { immediate: true })
    }

    onMounted(() => {
      validation.validate()
      setupPicker()
    })

    onBeforeUnmount(() => {
      validation.beforeUnmount()
      if (picker.value) {
        instInput().removeEventListener('blur', onBlur)
        picker.value.destroy()
        picker.value = null
      }
    })

    watch(() => props.disabled, watchDisabled)

    watch(
      () => props.modelValue,
      (newVal) => {
        if (newVal !== input.value.value) {
          picker.value.setDate(newVal)
          validation.validate(true)
        }
      }
    )

    watch(
      () => props.config,
      (newVal) => {
        let safeConfig = cloneObject(newVal)

        allEvents.forEach((hook) => {
          delete safeConfig[hook]
        })

        picker.value.set(safeConfig)

        configCallbacks.forEach((name) => {
          if (typeof safeConfig[name] !== 'undefined') {
            picker.value.set(name, safeConfig[name])
          }
        })
      },
      {
        deep: true,
      }
    )

    return {
      input,
      picker,
      isInline,
      instInput,
      getElement,
      onInput,
      onBlur,
      computedValue,
      open,
      ...validation,
    }
  },
})
</script>

<style lang="scss">
@import '../../scss/_settings.scss';
@import '~flatpickr/dist/flatpickr.min.css';
.ak-date-picker {
  background-color: #fff;
  border: 1px solid $border-input-color;
  border-radius: $base-border-radius;
  box-shadow: $form-box-shadow;
  box-sizing: border-box;
  font-family: $base-font-family;
  font-size: $base-font-size;
  width: 100%;
  padding: calc($spacing / 3) $spacing-small;
  min-height: $input-total-height;
  border-radius: $base-border-radius;
  transition-property: border-color, box-shadow, background-color, color;
  transition-duration: $transition-time;

  &::-webkit-input-placeholder {
    /* Chrome/Opera/Safari */
    color: $color-grey-light;
  }
  &::-moz-placeholder {
    /* Firefox 19+ */
    color: $color-grey-light;
  }
  &:-ms-input-placeholder {
    /* IE 10+ */
    color: $color-grey-light;
  }
  &:-moz-placeholder {
    /* Firefox 18- */
    color: $color-grey-light;
  }

  &:hover {
    border-color: darken($border-input-color, 15%);
  }

  &:focus {
    border-color: $color-action;
    box-shadow: $form-box-shadow-focus;
    outline: none;
  }

  &[read-only],
  &[readonly],
  &.is-readonly,
  &[disabled],
  &.is-disabled {
    background-color: $color-grey-lightest;
  }

  &[disabled],
  &.is-disabled {
    color: rgba($color-text, 0.625);
    border-color: lighten($border-input-color, 10%);
  }

  &--validation-danger {
    border-color: $color-danger;
  }
  &--validation-success {
    border-color: $color-success;
  }
  &--validation-warning {
    border-color: $color-warning;
  }

  &--inline {
    &.flatpickr-input {
      display: none;
    }
  }
}
.flatpickr-input.flatpickr-input[readonly]:not([disabled]),
input[type='text'][readonly].flatpickr-input:not([disabled]),
.flatpickr-input + .input:not([disabled]),
.flatpickr-input + input[type='text'][readonly].input:not([disabled]) {
  background-color: #fff;
}
.flatpickr-input.flatpickr-input[disabled] {
  background-color: $color-grey-lightest !important;
  cursor: default;
}
.flatpickr-current-month {
  padding-top: 5px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
}
.flatpickr-current-month input.cur-year {
  min-height: 0;
}
.flatpickr-monthDropdown-months {
  height: 30px !important;
}
.flatpickr-day {
  color: $color-text;
}
.flatpickr-day.today {
  border-color: $color-grey-light;
}
.flatpickr-day.inRange,
.flatpickr-day.nextMonthDay.inRange,
.flatpickr-day.nextMonthDay.today.inRange,
.flatpickr-day.nextMonthDay:focus,
.flatpickr-day.nextMonthDay:hover,
.flatpickr-day.prevMonthDay.inRange,
.flatpickr-day.prevMonthDay.today.inRange,
.flatpickr-day.prevMonthDay:focus,
.flatpickr-day.prevMonthDay:hover,
.flatpickr-day.today.inRange,
.flatpickr-day:focus,
.flatpickr-day:hover {
  background: $color-primary-light;
  border-color: $color-primary-light;
  color: black;
}
.flatpickr-day.inRange {
  box-shadow: -5px 0 0 $color-primary-light, 5px 0 0 $color-primary-light;
}
.flatpickr-day.endRange.startRange + .endRange:not(:nth-child(7n + 1)),
.flatpickr-day.selected.startRange + .endRange:not(:nth-child(7n + 1)),
.flatpickr-day.startRange.startRange + .endRange:not(:nth-child(7n + 1)) {
  box-shadow: -10px 0 0 $color-primary;
}
.flatpickr-day.endRange,
.flatpickr-day.endRange.inRange,
.flatpickr-day.endRange.nextMonthDay,
.flatpickr-day.endRange.prevMonthDay,
.flatpickr-day.endRange:focus,
.flatpickr-day.endRange:hover,
.flatpickr-day.selected,
.flatpickr-day.selected.inRange,
.flatpickr-day.selected.nextMonthDay,
.flatpickr-day.selected.prevMonthDay,
.flatpickr-day.selected:focus,
.flatpickr-day.selected:hover,
.flatpickr-day.startRange,
.flatpickr-day.startRange.inRange,
.flatpickr-day.startRange.nextMonthDay,
.flatpickr-day.startRange.prevMonthDay,
.flatpickr-day.startRange:focus,
.flatpickr-day.startRange:hover {
  background: $color-primary;
  border-color: $color-primary;
  color: #fff;
}
.flatpickr-current-month .flatpickr-monthDropdown-months,
.flatpickr-current-month input.cur-year {
  font-weight: 500;
}

.flatpickr-months .flatpickr-next-month:hover svg,
.flatpickr-months .flatpickr-prev-month:hover svg {
  fill: $color-primary;
}

.flatpickr-day.disabled,
.flatpickr-day.disabled:hover {
  cursor: default;
}

.flatpickr-calendar.inline {
  display: block !important;
  box-shadow: none;
  border: $base-border;
}

.ak-date-picker--has-preset-range {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.flatpickr-has-predefined-ranges {
  .flatpickr-months {
    position: relative;
  }

  .flat-pickr-ranges-nav {
    list-style-type: none;
    text-align: left;
    padding: 0;
    margin: 0;

    strong {
      color: $color-grey;
    }

    span {
      color: $color-grey-light;
    }

    li {
      border-bottom: 1px solid $color-grey-lighter;

      a {
        display: block;
        padding: 5px;
        cursor: pointer;
      }
    }

    li:hover,
    li.active {
      background-color: $color-grey-lightest;
    }

    li:last-child {
      border-bottom: 0px;
    }
  }
}
</style>
